import type { ReactNode } from 'react';

import { Body, cn, useContentMask } from '@growtherapy/sprout-ui';

import { LoadingSpinner } from '../../components/LoadingSpinner';
import type { AdmittedClientStatusResult } from './useAdmittedClientStatus';

const STATUS_COPY = {
  sending_invite: (_visitorName: ReactNode) => <>Sending Invitation…</>,
  preparing: (visitorName: ReactNode) => <>{visitorName} is preparing…</>,
  joining: (visitorName: ReactNode) => <>{visitorName} is connecting…</>,
  not_on_page: (visitorName: ReactNode) => <>{visitorName} may have left the page</>,
};

export function JoiningClientLoader({
  admittedClientStatus,
  onDarkSurface,
  visitorName,
}: {
  admittedClientStatus: AdmittedClientStatusResult;
  onDarkSurface?: boolean;
  visitorName: string;
}) {
  const { maskClassName, maskProps } = useContentMask({ mask: 'exclude' });
  const { status, showSpinner, shouldPulse } = admittedClientStatus;

  if (status === 'connected') {
    return null;
  }

  const spinnerClassName = cn('fa-spin text-neutral-900', {
    'rebrand:text-text-inverse': onDarkSurface,
    'text-text-muted rebrand:text-text-muted': shouldPulse,
  });

  const maskedName = (
    <span className={maskClassName} {...maskProps}>
      {visitorName}
    </span>
  );

  return (
    <div
      className={cn('flex gap-2 items-center text-small', {
        'rebrand:text-text-inverse': onDarkSurface,
        'animate-pulse text-text-muted rebrand:text-text-muted': shouldPulse,
      })}
    >
      {showSpinner ? <LoadingSpinner aria-hidden className={spinnerClassName} /> : null}
      <Body.Small
        aria-live='polite'
        className={cn({
          'rebrand:text-text-inverse': onDarkSurface,
          'rebrand:text-text-muted': shouldPulse,
        })}
        data-testid='joining-client-loader.status'
      >
        {STATUS_COPY[status](maskedName)}
      </Body.Small>
    </div>
  );
}
